import api from './api';
import {
  Vendor,
  CreateVendorRequest,
  PaginatedVendorResponse,
  User,
} from '../types';
import { API_ENDPOINTS } from '../constants';

interface VendorUsersResponse {
  vendor: { id: string; name: string; code?: string | null };
  users: User[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

const vendorService = {
  /**
   * List vendors with the headcount hired through each one.
   * The count is scoped by the caller's role on the backend, so a CLM sees
   * only their own cluster's staff in the number.
   */
  getVendors: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    is_active?: boolean;
  }): Promise<PaginatedVendorResponse> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: PaginatedVendorResponse;
    }>(API_ENDPOINTS.VENDORS.LIST, { params });
    return response.data.data;
  },

  getVendorById: async (id: string): Promise<Vendor> => {
    const response = await api.get<{ success: boolean; message: string; data: Vendor }>(
      API_ENDPOINTS.VENDORS.GET_BY_ID(id)
    );
    return response.data.data;
  },

  /**
   * Employees hired through a vendor.
   */
  getVendorUsers: async (
    id: string,
    params?: { page?: number; limit?: number; is_active?: boolean }
  ): Promise<VendorUsersResponse> => {
    const response = await api.get<{
      success: boolean;
      message: string;
      data: VendorUsersResponse;
    }>(API_ENDPOINTS.VENDORS.USERS(id), { params });
    return response.data.data;
  },

  createVendor: async (vendorData: CreateVendorRequest): Promise<Vendor> => {
    const response = await api.post<{ success: boolean; message: string; data: Vendor }>(
      API_ENDPOINTS.VENDORS.CREATE,
      vendorData
    );
    return response.data.data;
  },

  updateVendor: async (
    id: string,
    vendorData: Partial<CreateVendorRequest>
  ): Promise<Vendor> => {
    const response = await api.put<{ success: boolean; message: string; data: Vendor }>(
      API_ENDPOINTS.VENDORS.UPDATE(id),
      vendorData
    );
    return response.data.data;
  },

  /**
   * Soft delete. Employees keep their vendor link so "hired via" history is
   * preserved; the vendor just stops being offered for new staff.
   */
  deactivateVendor: async (id: string): Promise<{ linked_users: number }> => {
    const response = await api.delete<{
      success: boolean;
      message: string;
      data: { linked_users: number };
    }>(API_ENDPOINTS.VENDORS.DELETE(id));
    return response.data.data;
  },
};

export default vendorService;
